/**
 * CustomerImportSection — CSV klantimport wizard (Instellingen).
 *
 * Drie stappen:
 *   1. Upload CSV → backend preview (geen DB-mutatie).
 *   2. Preview-tabel met per-rij classificatie + per-rij actie kiezen.
 *   3. Commit → backend voert pas dan inserts uit.
 *
 * Privacy guardrails:
 *   - CSV wordt nooit opgeslagen in localStorage/sessionStorage.
 *   - Preview-records hebben backend-zijde een MongoDB TTL (24u).
 *   - Geen klantnamen/e-mails in console.log of error-toasts.
 */
import React, { useCallback, useEffect, useState } from "react";
import { adminApi, formatApiError } from "../../../context/AuthContext";
import { Button } from "../../ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../ui/select";
import { toast } from "sonner";
import { Upload, AlertTriangle, CheckCircle, XCircle, RefreshCw, History, FileUp, ShieldAlert } from "lucide-react";

const CLASS_LABELS = {
  new: { label: "Nieuw", color: "bg-emerald-100 text-emerald-800", icon: CheckCircle },
  merge_with_existing: { label: "Merge", color: "bg-yellow-100 text-yellow-800", icon: AlertTriangle },
  needs_review: { label: "Check adres", color: "bg-orange-100 text-orange-800", icon: AlertTriangle },
  duplicate_in_csv: { label: "Dubbel in CSV", color: "bg-gray-200 text-gray-700", icon: XCircle },
  injection_blocked: { label: "CSV-injection geblokkeerd", color: "bg-red-100 text-red-800", icon: ShieldAlert },
  invalid: { label: "Ongeldig", color: "bg-red-100 text-red-800", icon: XCircle },
};

const ACTION_OPTIONS = {
  new: [
    { value: "import", label: "Importeer als nieuwe klant" },
    { value: "skip", label: "Sla over" },
  ],
  merge_with_existing: [
    { value: "merge_into", label: "Koppel aan bestaande klant" },
    { value: "import", label: "Toch importeren als nieuwe klant" },
    { value: "skip", label: "Sla over" },
  ],
  needs_review: [
    { value: "skip", label: "Sla over" },
    { value: "import", label: "Toch importeren" },
    { value: "merge_into", label: "Koppel aan bestaande klant" },
  ],
  duplicate_in_csv: [
    { value: "skip", label: "Sla over (dubbel)" },
  ],
  injection_blocked: [
    { value: "skip", label: "Verplicht skippen (geblokkeerd)" },
  ],
  invalid: [
    { value: "skip", label: "Sla over (ongeldig)" },
  ],
};

export default function CustomerImportSection() {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [decisions, setDecisions] = useState({});
  const [committing, setCommitting] = useState(false);
  const [batches, setBatches] = useState([]);

  const loadBatches = useCallback(async () => {
    try {
      const r = await adminApi.get("/admin/customers/import/batches", { params: { limit: 20 } });
      setBatches(r.data);
    } catch (e) { /* niet kritiek */ }
  }, []);

  useEffect(() => { loadBatches(); }, [loadBatches]);

  const reset = () => {
    setFile(null);
    setPreview(null);
    setDecisions({});
  };

  const onUpload = async () => {
    if (!file) { toast.error("Kies eerst een CSV-bestand."); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await adminApi.post("/admin/customers/import/preview", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPreview(r.data);
      // Default-acties per rij obv classificatie.
      const def = {};
      for (const row of r.data.rows) {
        if (row.classification === "new") def[row.row_index] = "import";
        else if (row.classification === "merge_with_existing") def[row.row_index] = "merge_into";
        else def[row.row_index] = "skip";
      }
      setDecisions(def);
      toast.success(`Preview gereed: ${r.data.total_rows} rijen.`);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setUploading(false); }
  };

  const onCommit = async () => {
    if (!preview) return;
    const decisionList = preview.rows.map((row) => ({
      row_index: row.row_index,
      action: decisions[row.row_index] || "skip",
      merge_into_customer_id: row.match_customer_id || null,
    }));
    const willCreate = decisionList.filter((d) => d.action === "import").length;
    if (willCreate > 0 && !window.confirm(`${willCreate} nieuwe klanten worden aangemaakt. Doorgaan?`)) return;
    setCommitting(true);
    try {
      const r = await adminApi.post("/admin/customers/import/commit", {
        batch_id: preview.batch_id,
        decisions: decisionList,
      });
      toast.success(`Klaar — ${r.data.created} nieuw, ${r.data.merged} gemerged, ${r.data.skipped} overgeslagen.`);
      reset();
      loadBatches();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setCommitting(false); }
  };

  return (
    <div className="bg-white border border-gray-200 p-4 sm:p-6" data-testid="customer-import-section">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="font-chivo font-bold text-lg text-[#0A192F] flex items-center gap-2">
            <FileUp className="w-5 h-5 text-[#0055FF]" /> Klanten importeren via CSV
          </h2>
          <p className="text-sm text-gray-600 mt-1">
            Upload een CSV met klantgegevens. Het systeem toont eerst een preview met dedupe-controle —
            er wordt pas geïmporteerd na jouw bevestiging. Geen blinde import.
          </p>
        </div>
      </div>

      {!preview && (
        <div className="border-2 border-dashed border-gray-200 p-6 flex flex-col items-center gap-3" data-testid="customer-import-upload-box">
          <Upload className="w-10 h-10 text-gray-400" />
          <input
            type="file"
            accept=".csv,text/csv"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
            className="text-sm"
            data-testid="customer-import-file-input"
          />
          {file && <div className="text-xs text-gray-500">{file.name} — {(file.size / 1024).toFixed(1)} KB</div>}
          <Button
            onClick={onUpload}
            disabled={!file || uploading}
            className="bg-[#050505] hover:bg-[#0A192F] text-white rounded-none font-chivo font-bold uppercase text-xs"
            data-testid="customer-import-preview-button"
          >
            {uploading ? "Bezig…" : "Maak preview"}
          </Button>
          <p className="text-[11px] text-gray-500 max-w-md text-center">
            Verwachte kolommen: voornaam, achternaam, e-mail, telefoon, postcode, huisnummer, plaats, land, bedrijfsnaam, toevoeging.
            Maximum 5 MB, 5000 rijen.
          </p>
        </div>
      )}

      {preview && (
        <>
          <div className="flex flex-wrap items-center gap-3 mb-4">
            <span className="text-sm font-bold text-[#0A192F]">{preview.total_rows} rijen — </span>
            {Object.entries(preview.counts).map(([k, v]) => v > 0 ? (
              <span key={k} className={`inline-block px-2 py-0.5 text-[11px] uppercase tracking-wide font-bold ${CLASS_LABELS[k]?.color || "bg-gray-100 text-gray-700"}`} data-testid={`customer-import-count-${k}`}>
                {CLASS_LABELS[k]?.label || k}: {v}
              </span>
            ) : null)}
            <div className="ml-auto flex gap-2">
              <Button variant="outline" onClick={reset} className="rounded-none" data-testid="customer-import-cancel">
                Annuleren
              </Button>
              <Button
                onClick={onCommit}
                disabled={committing}
                className="bg-[#0055FF] hover:bg-[#003ecc] text-white rounded-none font-chivo font-bold uppercase text-xs"
                data-testid="customer-import-commit-button"
              >
                {committing ? "Bezig…" : "Bevestig & importeer"}
              </Button>
            </div>
          </div>

          <div className="border border-gray-200 overflow-x-auto">
            <table className="w-full text-sm" data-testid="customer-import-preview-table">
              <thead className="bg-gray-50 text-left text-[11px] uppercase tracking-wide font-chivo font-bold text-gray-700">
                <tr>
                  <th className="p-2 w-10">#</th>
                  <th className="p-2">Klant (preview)</th>
                  <th className="p-2 w-40">Status</th>
                  <th className="p-2 w-56">Actie</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {preview.rows.map((row) => {
                  const opts = ACTION_OPTIONS[row.classification] || ACTION_OPTIONS.invalid;
                  const cl = CLASS_LABELS[row.classification] || CLASS_LABELS.invalid;
                  const Icon = cl.icon;
                  const d = row.data || {};
                  const label = [d.first_name, d.last_name, d.company_name && `(${d.company_name})`]
                    .filter(Boolean).join(" ") || "—";
                  return (
                    <tr key={row.row_index} data-testid={`customer-import-row-${row.row_index}`}>
                      <td className="p-2 text-gray-400 text-xs">{row.row_index + 1}</td>
                      <td className="p-2 text-xs">
                        <div className="font-bold text-[#0A192F]">{label}</div>
                        <div className="text-gray-500 break-all">
                          {d.email || "—"} · {d.phone || "—"} · {d.postal_code || ""} {d.house_number || ""}{d.house_number_addition || ""} {d.city || ""}
                        </div>
                        {d.address && (
                          <div className="text-[11px] text-gray-500 mt-0.5" data-testid={`customer-import-row-${row.row_index}-street`}>
                            {d.address}{d.house_number ? ` ${d.house_number}` : ""}{d.house_number_addition ? ` ${d.house_number_addition}` : ""}
                          </div>
                        )}
                        {row.match_reason && (
                          <div className="text-[11px] text-gray-500 italic mt-0.5">↪ {row.match_reason}</div>
                        )}
                        {Array.isArray(row.warnings) && row.warnings.length > 0 && (
                          <div className="mt-1 space-y-0.5" data-testid={`customer-import-row-${row.row_index}-warnings`}>
                            {row.warnings.map((w, i) => (
                              <div key={i} className="inline-flex items-center gap-1 px-1.5 py-0.5 mr-1 bg-amber-100 text-amber-900 text-[10px] uppercase tracking-wide font-bold">
                                <AlertTriangle className="w-2.5 h-2.5" />{w}
                              </div>
                            ))}
                          </div>
                        )}
                      </td>
                      <td className="p-2">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-[10px] uppercase tracking-wide font-bold ${cl.color}`}>
                          <Icon className="w-3 h-3" /> {cl.label}
                        </span>
                      </td>
                      <td className="p-2">
                        <Select
                          value={decisions[row.row_index] || "skip"}
                          onValueChange={(v) => setDecisions({ ...decisions, [row.row_index]: v })}
                          disabled={row.classification === "injection_blocked" || row.classification === "invalid" || row.classification === "duplicate_in_csv"}
                        >
                          <SelectTrigger className="rounded-none h-8 text-xs" data-testid={`customer-import-action-${row.row_index}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {opts.map((opt) => (
                              <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* Geschiedenis */}
      <div className="mt-8">
        <div className="flex items-center justify-between mb-2">
          <h3 className="font-chivo font-bold text-sm text-[#0A192F] flex items-center gap-2">
            <History className="w-4 h-4" /> Import-batches
          </h3>
          <Button variant="outline" size="sm" onClick={loadBatches} className="rounded-none" data-testid="customer-import-history-refresh">
            <RefreshCw className="w-3.5 h-3.5 mr-1" /> Vernieuwen
          </Button>
        </div>
        {batches.length === 0 ? (
          <div className="text-xs text-gray-500 italic">Nog geen imports.</div>
        ) : (
          <div className="border border-gray-200 overflow-x-auto">
            <table className="w-full text-xs" data-testid="customer-import-history-table">
              <thead className="bg-gray-50 text-left uppercase tracking-wide font-chivo font-bold text-gray-700 text-[10px]">
                <tr>
                  <th className="p-2">Datum</th>
                  <th className="p-2">Door</th>
                  <th className="p-2">Bestand</th>
                  <th className="p-2">Status</th>
                  <th className="p-2">Resultaat</th>
                  <th className="p-2 w-28">Herstel</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {batches.map((b) => (
                  <tr key={b.id} data-testid={`customer-import-batch-${b.id}`}>
                    <td className="p-2">{new Date(b.started_at).toLocaleString("nl-NL")}</td>
                    <td className="p-2">{b.user_email}</td>
                    <td className="p-2">{b.file_name}</td>
                    <td className="p-2">{b.status}</td>
                    <td className="p-2">
                      {b.status === "committed" ? (
                        <>
                          <span className="text-green-700 font-bold">{b.created_count || 0} nieuw</span>
                          {(b.merged_count || 0) > 0 && <span className="text-gray-600"> · {b.merged_count} merge</span>}
                          {(b.skipped_count || 0) > 0 && <span className="text-gray-500"> · {b.skipped_count} skip</span>}
                        </>
                      ) : (
                        <span className="text-gray-500">— totaal {b.total_rows}</span>
                      )}
                    </td>
                    <td className="p-2">
                      {b.status === "committed" && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={async () => {
                            // P0 herstel — eerst aantallen ophalen zodat de
                            // admin precies weet wat wordt aangevuld.
                            let preview = null;
                            try {
                              const r = await adminApi.get(`/admin/customers/import/repair-batch/${b.id}/preview`);
                              preview = r.data;
                            } catch (e) {
                              toast.error(formatApiError(e));
                              return;
                            }
                            const lines = [];
                            lines.push(`Batch ${b.file_name} bevat ${preview.total_in_batch} klanten.`);
                            if (preview.missing_house_number_repairable > 0) {
                              lines.push(`• ${preview.missing_house_number_repairable} klanten missen huisnummer — te repareren uit straat.`);
                            }
                            if (preview.missing_house_number_unrepairable > 0) {
                              lines.push(`• ${preview.missing_house_number_unrepairable} klanten missen huisnummer — niet automatisch te repareren.`);
                            }
                            if (preview.missing_first_name_repairable > 0) {
                              lines.push(`• ${preview.missing_first_name_repairable} klanten missen voornaam — te repareren uit CSV-snapshot.`);
                            }
                            if (preview.missing_last_name_repairable > 0) {
                              lines.push(`• ${preview.missing_last_name_repairable} klanten missen achternaam — te repareren uit CSV-snapshot.`);
                            }
                            if (preview.missing_company_name_with_csv > 0) {
                              lines.push(`• ${preview.missing_company_name_with_csv} klanten missen bedrijfsnaam — te repareren uit CSV-snapshot.`);
                            }
                            if (preview.missing_display_name > 0) {
                              lines.push(`• ${preview.missing_display_name} klanten missen display_name (wordt opnieuw berekend).`);
                            }
                            if (preview.needs_manual_review > 0) {
                              lines.push(`• ${preview.needs_manual_review} klanten hebben geen naam in CSV-snapshot — handmatige controle nodig.`);
                            }
                            if (!preview.has_csv_snapshot) {
                              lines.push(`⚠ Deze batch heeft geen CSV-snapshot bewaard (oude import) — naamvelden kunnen niet automatisch worden hersteld.`);
                            }
                            const repairableTotal = (preview.missing_house_number_repairable || 0)
                              + (preview.missing_first_name_repairable || 0)
                              + (preview.missing_last_name_repairable || 0)
                              + (preview.missing_company_name_with_csv || 0)
                              + (preview.missing_display_name || 0);
                            if (repairableTotal === 0) {
                              lines.push(`Geen aanvulbare velden gevonden — herstel is veilig maar wijzigt niets.`);
                            }
                            lines.push("");
                            lines.push("Alleen lege velden worden aangevuld. Bestaande gegevens worden NIET overschreven. Geen dubbele klanten worden aangemaakt.");
                            lines.push("");
                            lines.push("Doorgaan?");
                            if (!window.confirm(lines.join("\n"))) return;
                            try {
                              const r = await adminApi.post(`/admin/customers/import/repair-batch/${b.id}`);
                              const namesPart = (r.data.names_repaired ?? 0) > 0
                                ? ` (${r.data.names_repaired} naam-velden)` : "";
                              toast.success(`Hersteld — ${r.data.repaired} klanten bijgewerkt${namesPart} (gescand: ${r.data.scanned})`);
                              loadBatches();
                            } catch (e) {
                              toast.error(formatApiError(e));
                            }
                          }}
                          className="rounded-none text-[10px] h-7"
                          data-testid={`customer-import-batch-repair-${b.id}`}
                        >
                          Herstel adres
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
